import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Sparkles, CalendarRange } from "lucide-react";
import { WeeklyTimetable } from "./WeeklyTimetable";
import { BookingModal } from "./BookingModal";

// Homepage booking section — cinematic header + custom WeeklyTimetable
// (Acuity stays behind the BookingModal lightbox; no large embedded iframe).
export const BookingPreview = () => {
  const [openFull, setOpenFull] = useState(false);

  return (
    <section
      id="bookings"
      data-focus="bookings"
      data-testid="booking-preview-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-black"
    >
      {/* Ambient lighting */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-brand-purpleDeep/20 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-brand-yellow/8 blur-3xl" />
      </div>
      <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 pb-8 border-b border-white/10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <Sparkles size={18} className="text-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Find a Class · Reserve Online
              </span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide leading-[0.95]">
              Pick a class.
              <br />
              <span className="text-brand-yellow text-glow-yellow">Lock</span> it in.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-zinc-300/90 text-base sm:text-lg leading-relaxed">
              Our weekly class schedule across <span className="text-white font-semibold">London &amp; Kent</span> —
              kids, adults and workshops, all bookable in seconds.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setOpenFull(true)}
                data-testid="booking-preview-open-modal-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
              >
                <Calendar size={14} /> Reserve a spot
              </button>
              <Link
                to="/schedule"
                data-testid="booking-preview-fullpage-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white font-bold uppercase text-xs tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow transition-all"
              >
                <CalendarRange size={14} /> Find a class
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <WeeklyTimetable variant="full" defaultFilter="all" limit={6} showCta />
        </motion.div>
      </div>

      <BookingModal
        open={openFull}
        onClose={() => setOpenFull(false)}
        title="Reserve a class"
        subtitle="Browse the full live schedule"
      />
    </section>
  );
};
